import { useCallback, useEffect, useRef, useState } from 'react';
import { getService, reorderSetlist } from './setlistApi';
import { idsOf, moveItem, sameIds } from './setlistOrder';

/**
 * Optimistische volgorde voor één setlist.
 *
 * - De UI past een verplaatsing direct toe; opslaan gebeurt na het loslaten (één verzoek per gebaar).
 * - Er is nooit meer dan één verzoek tegelijk onderweg. Verplaatsingen tijdens een lopend verzoek worden samengevoegd
 *   en daarna als één verzoek verstuurd, op basis van de laatst bevestigde volgorde.
 * - Een antwoord overschrijft de weergave alleen als er geen nieuwere lokale wijziging is (geen oud-over-nieuw).
 * - Mislukt opslaan, dan keert de lijst terug naar de laatst bevestigde volgorde, met een melding en "opnieuw proberen".
 * - 409 = iemand anders wijzigde de setlist: de actuele server-versie wordt geladen, niets wordt stilzwijgend overschreven.
 */
export default function useSetlistOrder({ serviceId, serverItems, onServerService }) {
  const [items, setItems] = useState(serverItems);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const itemsRef = useRef(serverItems);
  const committed = useRef(serverItems);
  const desired = useRef(null);
  const inFlight = useRef(false);
  const onServerServiceRef = useRef(onServerService);
  onServerServiceRef.current = onServerService;

  const show = useCallback((next) => { itemsRef.current = next; setItems(next); }, []);

  // Externe wijzigingen (toevoegen/verwijderen/herladen) volgen we alleen als er geen eigen wijziging onderweg is.
  useEffect(() => {
    if (inFlight.current || desired.current) return;
    committed.current = serverItems;
    show(serverItems);
  }, [serverItems, show]);

  const flush = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setSaving(true);
    try {
      while (desired.current) {
        const sent = desired.current;
        const base = idsOf(committed.current);
        if (sameIds(sent, base)) { desired.current = null; break; }
        try {
          const service = await reorderSetlist(serviceId, sent, base);
          committed.current = service.setlist || [];
          if (sameIds(desired.current, sent)) {
            desired.current = null;
            show(committed.current);
            onServerServiceRef.current?.(service);
          }
        } catch (failure) {
          desired.current = null;
          if (failure?.response?.status === 409) {
            try {
              const fresh = await getService(serviceId);
              committed.current = fresh.setlist || [];
              show(committed.current);
              onServerServiceRef.current?.(fresh);
              setError({ kind: 'conflict', message: 'De setlist is ondertussen door iemand anders gewijzigd. De actuele volgorde is geladen; herhaal je wijziging als die nog nodig is.' });
              break;
            } catch (reloadFailure) { /* val terug op herstel van de laatst bevestigde volgorde */ }
          }
          show(committed.current);
          setError({
            kind: 'failed', retryIds: sent,
            message: failure?.response?.data?.detail && failure.response.status < 500 ? `Volgorde niet opgeslagen: ${failure.response.data.detail}` : 'De nieuwe volgorde is niet opgeslagen. De vorige volgorde is hersteld.',
          });
          break;
        }
      }
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  }, [serviceId, show]);

  const move = useCallback((from, to) => {
    const next = moveItem(itemsRef.current, from, to);
    if (next === itemsRef.current) return false;
    setError(null);
    show(next);
    desired.current = idsOf(next);
    flush();
    return true;
  }, [flush, show]);

  const retry = useCallback(() => {
    const ids = error?.retryIds;
    if (!ids) return;
    const byId = new Map(committed.current.map((item) => [item.id, item]));
    if (ids.length !== byId.size || !ids.every((id) => byId.has(id))) { setError(null); return; }
    setError(null);
    const next = ids.map((id, index) => ({ ...byId.get(id), order: index + 1 }));
    show(next);
    desired.current = ids;
    flush();
  }, [error, flush, show]);

  return { items, saving, error, move, retry, dismissError: () => setError(null) };
}
